/**
 * How long a drone may go quiet before the dashboard stops presenting its
 * telemetry as current (#577).
 *
 * Drones report roughly once a second (GPS_UPDATE_INTERVAL=1s on the onboard
 * controller), so a missed minute is not a quiet moment — it is sixty missed
 * frames. On the homelab, drone-sim-dev-003 had been silent for **61 days** and
 * drew exactly like a live aircraft: an ordinary marker, a position, and a
 * battery percentage. The map presented all three as facts.
 *
 * One threshold, deliberately. "Quiet for 30 seconds mid-flight" and "silent
 * since July" are different in urgency and a two-tier treatment was considered;
 * this is the conservative first step, and the number is one constant to change.
 */
export const STALE_AFTER_MS = 60_000;

/**
 * Whether a drone's last report is old enough to stop trusting.
 *
 * An unparseable or missing timestamp counts as stale. That is the fail-safe
 * reading: absence of evidence that a drone is live is not evidence that it is,
 * and this system's rule is to fail loud rather than draw a confident marker
 * for an aircraft nobody has heard from (§10).
 *
 * A timestamp in the future is not stale — clock skew between the drone, the
 * API and the browser is ordinary, and treating a slightly-ahead clock as a
 * 61-day silence would be a worse lie than the one being fixed.
 */
export const isTelemetryStale = (
  lastUpdateTime: string | undefined,
  now: number
): boolean => {
  if (!lastUpdateTime) {
    return true;
  }
  const reportedAt = new Date(lastUpdateTime).getTime();
  if (Number.isNaN(reportedAt)) {
    return true;
  }
  return now - reportedAt > STALE_AFTER_MS;
};
